"use client";

import { useMemo, useState, type FormEvent } from "react";
import { getApiBaseUrl } from "@/lib/api";
import type { Locale } from "@/lib/i18n";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

export function ContactForm({ locale }: { locale: Locale }) {
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const t = useMemo(() => {
    const dict: Record<Locale, Record<string, string>> = {
      ru: {
        title: "Написать нам",
        name: "Имя",
        email: "Email",
        subject: "Тема",
        message: "Сообщение",
        send: "Отправить",
        sending: "Отправляем…",
        ok: "Спасибо! Сообщение отправлено.",
        err: "Не удалось отправить. Попробуйте ещё раз.",
      },
      en: {
        title: "Write to us",
        name: "Name",
        email: "Email",
        subject: "Subject",
        message: "Message",
        send: "Send",
        sending: "Sending…",
        ok: "Thank you! Your message has been sent.",
        err: "Could not send. Please try again.",
      },
      ky: {
        title: "Бизге жазыңыз",
        name: "Аты-жөнү",
        email: "Email",
        subject: "Тема",
        message: "Билдирүү",
        send: "Жөнөтүү",
        sending: "Жөнөтүлүүдө…",
        ok: "Рахмат! Билдирүү жөнөтүлдү.",
        err: "Жөнөтүү мүмкүн болгон жок. Кайра аракет кылыңыз.",
      },
    };
    return dict[locale] ?? dict.ru;
  }, [locale]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus({ kind: "submitting" });

    try {
      const base = getApiBaseUrl().replace(/\/+$/, "");
      const path = "/api/v1/contact/";
      const url = base ? `${base}${path}` : path;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Language": locale,
        },
        body: JSON.stringify({ name, email, subject, message }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        setStatus({
          kind: "error",
          message: text ? `${t.err} (${res.status})` : t.err,
        });
        return;
      }

      setStatus({ kind: "success" });
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
    } catch {
      setStatus({ kind: "error", message: t.err });
    }
  }

  return (
    <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
        <Field label={t.name}>
          <input value={name} onChange={(e) => setName(e.target.value)} required className="input" />
        </Field>
        <Field label={t.email}>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            type="email"
            className="input"
          />
        </Field>
      </div>
      <Field label={t.subject}>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} required className="input" />
      </Field>
      <Field label={t.message}>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          rows={6}
          className="input"
        />
      </Field>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button type="submit" disabled={status.kind === "submitting"} className="btn btnPrimary">
          {status.kind === "submitting" ? t.sending : t.send}
        </button>

        {status.kind === "success" ? <span className="muted">{t.ok}</span> : null}
        {status.kind === "error" ? <span className="muted">{status.message}</span> : null}
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="muted" style={{ fontSize: 13 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

